'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import { Media } from '@/components/Media'
import { cn } from '@/lib/utils'
import type { Project } from '@/payload-types'

export type SimpleProjectCardProject = Pick<
  Project,
  'id' | 'slug' | 'title' | 'shortDescription' | 'place'
> & {
  featuredImage?: Project['featuredImage'] | null
  projectDate?: Project['projectDate']
}

interface SimpleProjectCardProps {
  project: SimpleProjectCardProject
  sourceBlock?: string
}

export default function SimpleProjectCard({ project, sourceBlock }: SimpleProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const router = useRouter()

  const handleCardClick = () => {
    router.push(`/projekte/${project.slug}`)
  }

  return (
    <motion.div
      className="relative h-[424px] w-full overflow-hidden cursor-pointer"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleCardClick}
      data-card-type="project"
      data-card-slug={project.slug ?? undefined}
      data-card-block={sourceBlock}
    >
      {/* Background Image (scales on hover, container stays fixed) */}
      <motion.div
        className="absolute inset-0"
        animate={{ scale: isHovered ? 1.04 : 1 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {typeof project.featuredImage === 'object' && project.featuredImage ? (
          <Media
            resource={project.featuredImage}
            alt={project.title}
            fill
            pictureClassName="block h-full w-full"
            imgClassName="h-full w-full object-cover"
            size="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-sm">Kein Bild verfügbar</span>
          </div>
        )}
      </motion.div>

      {/* Overlay */}
      <div
        className={cn(
          'absolute inset-0 transition-all duration-300',
          isHovered
            ? 'bg-gradient-to-b from-transparent to-overlay-dark'
            : 'bg-gradient-to-b from-transparent to-overlay-light opacity-0',
        )}
      >
        {/* Content */}
        <div className="absolute bottom-16 left-0 right-0 p-6">
          <div className="text-center">
            <h3 className="title-md mb-1 on-dark uppercase">{project.title}</h3>
            {project.place && (
              <div className="flex items-center justify-center gap-3 body-base on-dark">
                <span>{project.place}</span>
              </div>
            )}
          </div>
        </div>

        {/* Link button - positioned at bottom with proper spacing */}
        <div className="absolute bottom-4 left-0 right-0 px-6 flex justify-center">
          <LinkWithIcon
            href={`/projekte/${project.slug}`}
            text="Projekt ansehen"
            variant="link"
            color="white"
            size="sm"
            className="caption-lg px-2 py-1"
          />
        </div>
      </div>
    </motion.div>
  )
}
